import { useMemo } from "react";
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  subDays,
  subWeeks,
  subMonths,
  format,
} from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CalendarBlank } from "@phosphor-icons/react";

/**
 * DateRangePresets
 *
 * Reusable date-range input with preset shortcuts — used across pub
 * and admin report tabs. Emits ISO `yyyy-MM-dd` strings via
 * `onChange({ start, end, presetKey })`.
 *
 * Presets cover the cases Nate requested: Today, Yesterday, This week,
 * Last week, This month, Previous month, Year-to-date. Plus "Custom"
 * which exposes manual <input type="date"> pickers, and "All time"
 * which clears the filter.
 */
const PRESETS = [
  { key: "all", label: "All time" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_7", label: "Last 7 days" },
  { key: "last_30", label: "Last 30 days" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "ytd", label: "Year to date" },
  { key: "custom", label: "Custom range" },
];

const fmt = (d) => format(d, "yyyy-MM-dd");

export function presetToRange(preset) {
  const now = new Date();
  switch (preset) {
    case "today":
      return { start: fmt(startOfDay(now)), end: fmt(endOfDay(now)) };
    case "yesterday": {
      const y = subDays(now, 1);
      return { start: fmt(startOfDay(y)), end: fmt(endOfDay(y)) };
    }
    case "last_7": {
      // Trailing 7 calendar days inclusive of today (today − 6 → today).
      const start = subDays(now, 6);
      return { start: fmt(startOfDay(start)), end: fmt(endOfDay(now)) };
    }
    case "last_30": {
      // Trailing 30 calendar days inclusive of today.
      const start = subDays(now, 29);
      return { start: fmt(startOfDay(start)), end: fmt(endOfDay(now)) };
    }
    case "this_week":
      return { start: fmt(startOfWeek(now, { weekStartsOn: 1 })), end: fmt(endOfWeek(now, { weekStartsOn: 1 })) };
    case "last_week": {
      const w = subWeeks(now, 1);
      return { start: fmt(startOfWeek(w, { weekStartsOn: 1 })), end: fmt(endOfWeek(w, { weekStartsOn: 1 })) };
    }
    case "this_month":
      return { start: fmt(startOfMonth(now)), end: fmt(endOfMonth(now)) };
    case "last_month": {
      const m = subMonths(now, 1);
      return { start: fmt(startOfMonth(m)), end: fmt(endOfMonth(m)) };
    }
    case "ytd":
      return { start: fmt(startOfYear(now)), end: fmt(endOfDay(now)) };
    case "custom":
    case "all":
    default:
      return { start: "", end: "" };
  }
}

export default function DateRangePresets({
  preset = "all",
  start = "",
  end = "",
  onChange,
  className = "",
  testId = "date-range-presets",
}) {
  const today = useMemo(() => fmt(new Date()), []);

  const onPresetChange = (key) => {
    if (key === "custom") {
      // Keep current start/end when entering custom; if both empty, default
      // to "this month" so the picker has sensible initial values.
      if (!start && !end) {
        const m = presetToRange("this_month");
        onChange?.({ preset: "custom", start: m.start, end: m.end });
      } else {
        onChange?.({ preset: "custom", start, end });
      }
      return;
    }
    const r = presetToRange(key);
    onChange?.({ preset: key, start: r.start, end: r.end });
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid={testId}>
      <Select value={preset} onValueChange={onPresetChange}>
        <SelectTrigger className="h-9 w-[150px] text-sm bg-white" data-testid={`${testId}-select`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {preset === "custom" && (
        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-md px-2.5 py-1">
          <CalendarBlank size={13} className="text-slate-400" />
          <Input
            type="date"
            value={start}
            max={end || today}
            onChange={(e) => onChange?.({ preset: "custom", start: e.target.value, end })}
            className="h-7 w-[130px] text-xs border-0 p-0 focus-visible:ring-0"
            data-testid={`${testId}-start`}
          />
          <span className="text-slate-300">→</span>
          <Input
            type="date"
            value={end}
            min={start}
            max={today}
            onChange={(e) => onChange?.({ preset: "custom", start, end: e.target.value })}
            className="h-7 w-[130px] text-xs border-0 p-0 focus-visible:ring-0"
            data-testid={`${testId}-end`}
          />
        </div>
      )}
      {preset !== "all" && preset !== "custom" && (start || end) && (
        <span className="text-[11px] text-slate-500 font-mono">{start} → {end}</span>
      )}
      {preset !== "all" && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange?.({ preset: "all", start: "", end: "" })}
          className="h-7 text-xs text-slate-500"
          data-testid={`${testId}-clear`}
        >
          Clear
        </Button>
      )}
    </div>
  );
}
